/* KONG HEADER v2 — vanilla, no deps. Everything is progressive: without this file the links still navigate. */
(() => {
  if (customElements.get('kong-header')) return;

  const mq = (q) => window.matchMedia(q);
  const DESKTOP = mq('(min-width: 1200px)');
  const FINE = mq('(hover: hover) and (pointer: fine)');
  const REDUCE = mq('(prefers-reduced-motion: reduce)');
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').trim();
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class KongHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this;
      this.bar = this.querySelector('.kh');
      this.initScroll();
      this.initMega();
      this.initIndicator();
      this.initMagnetic();
      this.initFilters();
      this.initDrawer();
      this.initCmd();
    }

    /* ---------- Scroll: translucent bar + hide on scroll down (mobile) ---------- */
    initScroll() {
      const hideMobile = this.dataset.hideMobile === 'true';
      let lastY = window.scrollY, ticking = false;
      const update = () => {
        ticking = false;
        const y = window.scrollY;
        this.classList.toggle('kh--scrolled', y > 8);
        if (hideMobile && !DESKTOP.matches && !this.isBusy()) {
          if (y > lastY + 6 && y > 140) this.section.classList.add('kh-is-hidden');
          else if (y < lastY - 6 || y < 140) this.section.classList.remove('kh-is-hidden');
        } else {
          this.section.classList.remove('kh-is-hidden');
        }
        lastY = y;
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
    }
    isBusy() {
      return this.classList.contains('kh--mega') || (this.drawer && this.drawer.open) || (this.cmd && this.cmd.open);
    }

    /* ---------- Mega: single container that morphs between panels ---------- */
    initMega() {
      this.mega = this.querySelector('[data-kh-mega]');
      if (!this.mega) return;
      this.triggers = Array.from(this.querySelectorAll('[data-kh-trigger]'));
      this.panels = new Map(Array.from(this.mega.querySelectorAll('[data-kh-panel]')).map((p) => [p.dataset.khPanel, p]));
      this.activeKey = null;
      let openT, closeT;

      const open = (key) => {
        clearTimeout(closeT);
        if (key === this.activeKey) return;
        const panel = this.panels.get(key);
        if (!panel) return;
        const prevIdx = this.activeKey ? Number(this.activeKey) : null;
        const dir = prevIdx === null ? 0 : Number(key) > prevIdx ? 1 : -1;
        this.panels.forEach((p) => {
          if (p === panel) return;
          p.style.setProperty('--dir', String(-dir || 1));
          p.classList.remove('is-active');
        });
        panel.style.setProperty('--dir', String(dir || 0));
        panel.querySelectorAll('img[data-kh-src]').forEach((img) => { img.src = img.dataset.khSrc; img.removeAttribute('data-kh-src'); });
        panel.classList.add('is-active');
        const h = Math.min(panel.scrollHeight, this.mega.clientHeight);
        this.style.setProperty('--kh-mega-h', h + 'px');
        this.triggers.forEach((t) => {
          const on = t.dataset.khTrigger === key;
          t.setAttribute('aria-expanded', String(on));
          t.closest('.kh__item').classList.toggle('is-open', on);
        });
        this.activeKey = key;
        this.classList.add('kh--mega');
        this.moveIndicator(this.querySelector(`[data-kh-trigger="${key}"]`));
      };
      const close = () => {
        clearTimeout(openT);
        if (!this.activeKey) return;
        this.classList.remove('kh--mega');
        this.style.setProperty('--kh-mega-h', '0px');
        this.triggers.forEach((t) => { t.setAttribute('aria-expanded', 'false'); t.closest('.kh__item').classList.remove('is-open'); });
        this.panels.forEach((p) => p.classList.remove('is-active'));
        this.activeKey = null;
        this.restIndicator();
      };
      this.closeMega = close;

      this.triggers.forEach((t) => {
        const key = t.dataset.khTrigger;
        t.addEventListener('pointerenter', (e) => {
          if (!DESKTOP.matches || e.pointerType !== 'mouse') return;
          clearTimeout(openT); clearTimeout(closeT);
          openT = setTimeout(() => open(key), this.activeKey ? 0 : 70);
        });
        /* Touch / pen on desktop widths: first tap opens, second tap follows the link */
        t.addEventListener('click', (e) => {
          if (!DESKTOP.matches) return;
          if (this.activeKey !== key && !FINE.matches) { e.preventDefault(); open(key); }
        });
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === ' ') {
            e.preventDefault(); open(key);
            const first = this.panels.get(key).querySelector('a, input, button');
            if (first) requestAnimationFrame(() => first.focus());
          }
        });
      });
      this.querySelectorAll('.kh__item:not([data-kh-item]) .kh__link').forEach((l) =>
        l.addEventListener('pointerenter', () => { if (DESKTOP.matches) closeT = setTimeout(close, 120); }));
      this.bar.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { clearTimeout(openT); closeT = setTimeout(close, 180); } });
      this.bar.addEventListener('pointerenter', () => clearTimeout(closeT));
      this.querySelector('.kh-dim').addEventListener('click', close);
      this.addEventListener('focusout', (e) => { if (this.activeKey && !this.bar.contains(e.relatedTarget)) close(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activeKey) {
          const trig = this.querySelector(`[data-kh-trigger="${this.activeKey}"]`);
          close(); if (trig) trig.focus();
        }
      });
      DESKTOP.addEventListener('change', close);

      /* Spotlight border on tiles (#62): rect cached per hover, one write per frame */
      this.mega.addEventListener('pointermove', (e) => {
        const tile = e.target.closest('.kh-tile');
        if (!tile) return;
        if (tile !== this._spot) { this._spot = tile; this._spotRect = tile.getBoundingClientRect(); }
        const r = this._spotRect;
        tile.style.setProperty('--mx', (e.clientX - r.left) + 'px');
        tile.style.setProperty('--my', (e.clientY - r.top) + 'px');
      }, { passive: true });
    }

    /* ---------- Sliding indicator under the nav (#20) ---------- */
    initIndicator() {
      this.ind = this.querySelector('[data-kh-ind]');
      const menu = this.querySelector('[data-kh-menu]');
      if (!this.ind || !menu) return;
      this.currentLink = menu.querySelector('.kh__link[aria-current="page"]');
      menu.querySelectorAll('.kh__link').forEach((l) => l.addEventListener('pointerenter', () => this.moveIndicator(l)));
      menu.addEventListener('pointerleave', () => { if (!this.activeKey) this.restIndicator(); });
      const rest = () => this.restIndicator(true);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(rest); else rest();
      window.addEventListener('resize', rest, { passive: true });
    }
    moveIndicator(link, instant) {
      if (!this.ind || !link) return;
      const x = link.offsetLeft + 8, w = Math.max(link.offsetWidth - 16, 12);
      if (instant) this.ind.style.transition = 'none';
      this.ind.style.setProperty('--x', x + 'px');
      this.ind.style.setProperty('--s', String(w / 100));
      this.ind.classList.add('is-on');
      if (instant) { void this.ind.offsetWidth; this.ind.style.transition = ''; }
    }
    restIndicator(instant) {
      if (!this.ind) return;
      if (this.currentLink) this.moveIndicator(this.currentLink, instant);
      else this.ind.classList.remove('is-on');
    }

    /* ---------- Magnetic buttons with lerp (#19) — fine pointers only ---------- */
    initMagnetic() {
      if (this.dataset.magnetic !== 'true' || REDUCE.matches || !FINE.matches) return;
      this.querySelectorAll('[data-kh-mag]').forEach((el) => {
        let rect, tx = 0, ty = 0, cx = 0, cy = 0, raf = 0, inside = false;
        const loop = () => {
          cx += (tx - cx) * 0.2; cy += (ty - cy) * 0.2;
          el.style.transform = `translate3d(${cx.toFixed(2)}px, ${cy.toFixed(2)}px, 0)`;
          if (inside || Math.abs(cx) > 0.1 || Math.abs(cy) > 0.1) raf = requestAnimationFrame(loop);
          else { raf = 0; el.style.transform = ''; el.classList.remove('is-mag'); }
        };
        el.addEventListener('pointerenter', () => { rect = el.getBoundingClientRect(); inside = true; el.classList.add('is-mag'); if (!raf) raf = requestAnimationFrame(loop); });
        el.addEventListener('pointermove', (e) => {
          if (!rect) return;
          tx = (e.clientX - rect.left - rect.width / 2) * 0.28;
          ty = (e.clientY - rect.top - rect.height / 2) * 0.28;
        }, { passive: true });
        el.addEventListener('pointerleave', () => { inside = false; tx = 0; ty = 0; rect = null; if (!raf) raf = requestAnimationFrame(loop); });
      });
    }

    /* ---------- Brand filter + A–Z (mega and drawer) ---------- */
    initFilters() {
      this.querySelectorAll('[data-kh-filter]').forEach((input) => {
        const scope = input.closest('.kh-panel__main') || input.closest('.kh-acc');
        const list = scope.querySelector('[data-kh-brands]');
        const items = Array.from(list.querySelectorAll('.kh-brand-li')).map((li) => ({ li, name: norm(li.dataset.khName) }));
        const groups = Array.from(list.querySelectorAll('[data-kh-group]'));
        const letters = Array.from(scope.querySelectorAll('[data-kh-letter]'));
        const empty = scope.querySelector('[data-kh-empty]');
        input.addEventListener('input', () => {
          const q = norm(input.value);
          let shown = 0;
          /* match from the start of any word: "rot" finds ROTTERDAM, not MYPROTEIN */
          items.forEach(({ li, name }) => { const ok = !q || name.startsWith(q) || name.includes(' ' + q); li.hidden = !ok; if (ok) shown++; });
          groups.forEach((g) => {
            const any = !!g.querySelector('.kh-brand-li:not([hidden])');
            g.hidden = !any;
            const btn = letters.find((b) => b.dataset.khLetter === g.dataset.khGroup);
            if (btn) btn.disabled = !any;
          });
          if (empty) empty.hidden = shown > 0;
          const panel = input.closest('.kh-panel');
          if (panel && panel.classList.contains('is-active')) {
            this.style.setProperty('--kh-mega-h', Math.min(panel.scrollHeight, this.mega.clientHeight) + 'px');
          }
        });
        input.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          const first = list.querySelector('.kh-brand-li:not([hidden]) a');
          if (first) first.click();
        });
        letters.forEach((btn) => btn.addEventListener('click', () => {
          const g = groups.find((x) => x.dataset.khGroup === btn.dataset.khLetter);
          const panel = btn.closest('.kh-panel');
          if (g && panel) panel.scrollTo({ top: g.offsetTop - 16, behavior: REDUCE.matches ? 'auto' : 'smooth' });
        }));
      });
    }

    /* ---------- Drawer (<dialog>) with 0fr→1fr accordions (#29) ---------- */
    initDrawer() {
      this.drawer = this.querySelector('.kh-drawer');
      const openBtn = this.querySelector('[data-kh-drawer-open]');
      if (!this.drawer || !openBtn) return;
      openBtn.addEventListener('click', () => {
        this.drawer.showModal();
        openBtn.setAttribute('aria-expanded', 'true');
      });
      this.drawer.addEventListener('close', () => openBtn.setAttribute('aria-expanded', 'false'));
      this.drawer.querySelector('[data-kh-drawer-close]').addEventListener('click', () => this.drawer.close());
      this.drawer.addEventListener('click', (e) => {
        const t = e.target.closest('[data-kh-acc]');
        if (!t) return;
        t.setAttribute('aria-expanded', String(t.getAttribute('aria-expanded') !== 'true'));
      });
      DESKTOP.addEventListener('change', (e) => { if (e.matches && this.drawer.open) this.drawer.close(); });
    }

    /* ---------- Command palette search (#28 + #22 + #67) ---------- */
    initCmd() {
      this.cmd = this.querySelector('[data-kh-cmd]');
      if (!this.cmd) return;
      const input = this.cmd.querySelector('[data-kh-q]');
      const list = this.cmd.querySelector('[data-kh-results]');
      const quick = this.cmd.querySelector('[data-kh-quick]');
      const status = this.cmd.querySelector('[data-kh-status]');
      const suggestUrl = this.dataset.suggestUrl || '/search/suggest';
      const searchUrl = this.dataset.searchUrl || '/search';
      const cache = new Map();
      let ctrl, timer, sel = -1;

      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'MXN';
      const fmt = (v) => {
        const n = Number(v);
        if (!isFinite(n)) return '';
        try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n); }
        catch (_) { return '$' + n; }
      };
      const sized = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '');

      const open = (from) => {
        if (this.closeMega) this.closeMega();
        if (this.drawer && this.drawer.open) this.drawer.close();
        if (from && from.getBoundingClientRect) {
          const r = from.getBoundingClientRect();
          this.cmd.style.setProperty('--ox', (r.left + r.width / 2) + 'px');
          this.cmd.style.setProperty('--oy', (r.top + r.height / 2) + 'px');
        }
        this.cmd.showModal();
        input.focus();
        if (input.value) input.select();
      };
      this.openCmd = open;

      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-kh-cmd-open]');
        if (!t || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        open(t);
      });
      this.cmd.querySelector('[data-kh-cmd-close]').addEventListener('click', () => this.cmd.close());
      document.addEventListener('keydown', (e) => {
        const tag = (e.target.tagName || '').toLowerCase();
        const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable;
        if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.cmd.open ? this.cmd.close() : open(this.querySelector('.kh__search-hint')); }
        else if (e.key === '/' && !typing && !this.cmd.open) { e.preventDefault(); open(this.querySelector('.kh__search-hint')); }
      });
      if (/Mac|iPhone|iPad/.test(navigator.platform || '')) this.querySelectorAll('.kh__search-hint kbd').forEach((k) => { k.textContent = '⌘K'; });

      const options = () => Array.from(list.querySelectorAll('[role="option"]'));
      const select = (i) => {
        const opts = options();
        if (!opts.length) { sel = -1; input.removeAttribute('aria-activedescendant'); return; }
        sel = (i + opts.length) % opts.length;
        opts.forEach((o, j) => o.setAttribute('aria-selected', String(j === sel)));
        input.setAttribute('aria-activedescendant', opts[sel].id);
        opts[sel].scrollIntoView({ block: 'nearest' });
      };

      const render = (q, data) => {
        const r = (data && data.resources && data.resources.results) || {};
        const products = r.products || [], collections = r.collections || [], queries = r.queries || [];
        let html = '', i = 0;
        const id = () => 'kh-opt-' + (i++);
        products.slice(0, 6).forEach((p) => {
          const img = (p.featured_image && p.featured_image.url) || p.image || '';
          const cmp = Number(p.compare_at_price_max || 0) > Number(p.price || 0) ? `<s>${fmt(p.compare_at_price_max)}</s>` : '';
          html += `<li><a class="kh-res" role="option" id="${id()}" aria-selected="false" href="${esc(p.url)}">
            ${img ? `<img class="kh-res__img" src="${esc(sized(img, 128))}" alt="" width="64" height="64" loading="lazy">` : '<span class="kh-res__img"></span>'}
            <span><span class="kh-res__t">${esc(p.title)}</span><span class="kh-res__v">${esc(p.vendor || '')}</span></span>
            <span class="kh-res__p">${fmt(p.price)}${cmp}<span class="kh-res__stock${p.available ? '' : ' kh-res__stock--out'}">${p.available ? 'En stock' : 'Agotado'}</span></span>
          </a></li>`;
        });
        collections.slice(0, 3).forEach((c) => {
          html += `<li><a class="kh-res kh-res--row" role="option" id="${id()}" aria-selected="false" href="${esc(c.url)}">
            <svg class="kh-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/></svg>
            <span class="kh-res__t">${esc(c.title)}</span><span class="kh-res__v">Categoría</span></a></li>`;
        });
        queries.slice(0, 3).forEach((s) => {
          html += `<li><a class="kh-res kh-res--row" role="option" id="${id()}" aria-selected="false" href="${esc(s.url)}">
            <svg class="kh-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/></svg>
            <span class="kh-res__t">${esc(s.text)}</span><span></span></a></li>`;
        });
        if (!html) html = `<li class="kh-empty">Sin resultados para “${esc(q)}”. Prueba con la marca o el tipo de producto.</li>`;
        html += `<li><a class="kh-cmd__all kh-press" href="${esc(searchUrl)}?q=${encodeURIComponent(q)}&options%5Bprefix%5D=last">Ver todos los resultados</a></li>`;
        list.innerHTML = html;
        sel = -1;
        input.setAttribute('aria-expanded', 'true');
        status.textContent = products.length ? `${products.length} productos encontrados` : 'Sin resultados';
      };

      const search = (q) => {
        if (cache.has(q)) { render(q, cache.get(q)); return; }
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        list.innerHTML = '<li class="kh-sk"></li><li class="kh-sk"></li><li class="kh-sk"></li>';
        const url = `${suggestUrl}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,query&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,tag,variants.title`;
        fetch(url, { signal: ctrl.signal })
          .then((r) => r.json())
          .then((data) => { cache.set(q, data); if (norm(input.value) === norm(q)) render(q, data); })
          .catch((err) => { if (err.name !== 'AbortError') list.innerHTML = '<li class="kh-empty">No pudimos buscar ahora. Presiona Enter para ver resultados.</li>'; });
      };

      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        quick.hidden = q.length >= 2;
        if (q.length < 2) { list.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
        timer = setTimeout(() => search(q), 140);
      });
      input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); select(sel + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); select(sel - 1); }
        else if (e.key === 'Enter' && sel > -1) { e.preventDefault(); const o = options()[sel]; if (o) o.click(); }
      });
      this.cmd.addEventListener('click', (e) => {
        const term = e.target.closest('[data-kh-term]');
        if (!term || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        input.value = term.dataset.khTerm;
        input.dispatchEvent(new Event('input'));
        input.focus();
      });
    }
  }

  customElements.define('kong-header', KongHeader);
})();
